(() => {
    'use strict';

    /* ------------------------------------------------------------------
     * Page transitions.
     *
     * swup fetches the next page and swaps <main>, so navigation animates
     * instead of blanking. Firefox has no cross-document View Transitions, which
     * is why this is a library rather than four lines of CSS — swup uses the
     * native API where it exists and its own animation everywhere else.
     *
     * Swapping the page instead of loading it means everything a real navigation
     * used to do for free now has an owner. Each item below is something that
     * silently breaks otherwise, not defensive decoration:
     *   - analytics stops recording anything past the first page,
     *   - the theme's scripts keep listeners bound to a page that is gone,
     *   - the reveal system's before-paint failsafe never re-arms,
     *   - <head> keeps the old page's title, canonical and lang.
     * ---------------------------------------------------------------- */

    const root = document.documentElement;

    // Nothing to enhance, or the visitor asked for less movement: leave real navigations
    // alone. A full page load is the accessible default, not a degraded one.
    if (typeof window.Swup !== 'function') {
        return;
    }

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        return;
    }

    const settings = window.MBTransitions || {};

    const plugins = [];

    // Keeps <title>, canonical, OG tags and <html lang> honest — Yoast writes them per
    // page, and the language switcher depends on lang being right.
    if (typeof window.SwupHeadPlugin === 'function') {
        plugins.push(new window.SwupHeadPlugin({ persistTags: 'style[data-swup-theme]' }));
    }

    // Moves focus to the new page and announces it. Without this a swap is silent: a
    // screen-reader user hears nothing and keyboard focus stays where the old page was.
    if (typeof window.SwupA11yPlugin === 'function') {
        plugins.push(new window.SwupA11yPlugin({
            announcementTemplate: settings.announcement || '%s',
            urlTemplate: settings.announcementUrl || '%s',
        }));
    }

    const swup = new window.Swup({
        // The header is swapped too: the active menu item and, more importantly, the
        // language switcher's href are per-page. Leaving it out would have the Dutch
        // portfolio page offering a link to the English page you were on two clicks ago.
        containers: ['#swup', '#site-header'],
        animationSelector: '.mb-page',
        // Ignore anything that is not a page of this site: admin, downloads, the
        // language switcher (a swap between languages is a different document in every
        // sense), and anything explicitly opted out.
        //
        // Dropdown toggles are excluded too. A menu item with children is an <a> with a
        // real href that Bootstrap turns into a button: it calls preventDefault and
        // opens the submenu. swup does not consult defaultPrevented, so it navigated
        // anyway -- the submenu opened and the page left underneath it in the same
        // click, on the desktop menu as well as in the offcanvas.
        linkSelector: 'a[href]:not([data-no-swup]):not([target]):not([download])' +
            ':not([href*="/wp-admin"]):not([href*="/wp/"]):not([href^="mailto:"])' +
            ':not([href^="tel:"]):not(.mb-language-switcher-link)' +
            ':not([data-bs-toggle="dropdown"])',
        plugins,
    });

    /* ------------------------------------------------------------------
     * Re-arm everything a real page load used to do.
     * ---------------------------------------------------------------- */

    swup.hooks.on('visit:start', () => {
        // The mobile menu lives inside the header container. Swapping it out from under
        // an open offcanvas leaves Bootstrap holding a detached element and a backdrop
        // with nothing behind it, so close it properly first.
        const offcanvas = document.querySelector('.offcanvas.show, .offcanvas-lg.show');

        if (offcanvas && window.bootstrap && window.bootstrap.Offcanvas) {
            const instance = window.bootstrap.Offcanvas.getInstance(offcanvas);
            if (instance) {
                instance.hide();
            }
        }

        // The dialog lives on <body>, so a navigation while it is open would leave it
        // hanging over the new page.
        if (window.MB && window.MB.lightbox) {
            window.MB.lightbox.close();
        }

        // Listeners belonging to the page being left. The scroll/resize pair in reveal.js
        // and the pageshow listener in archive-toolbar.js are on window, so they outlive
        // their own page unless taken down here.
        if (window.MB && window.MB.reveal) {
            window.MB.reveal.destroy();
        }

        if (window.MB && window.MB.archiveToolbar) {
            window.MB.archiveToolbar.destroy();
        }

        // A Bootstrap Carousel instance holds an interval timer and window listeners of
        // its own, which would keep ticking against elements that left with the page.
        if (window.MB && window.MB.carousel) {
            window.MB.carousel.destroy();
        }

        // The incoming page's blocks start hidden again, so the failsafe that guarantees
        // they cannot stay that way has to be re-armed for this navigation too.
        root.removeAttribute('data-reveal-ready');
    });

    swup.hooks.on('page:view', () => {
        if (window.MB) {
            if (window.MB.reveal) {
                window.MB.reveal.init();
            }

            if (window.MB.lightbox) {
                window.MB.lightbox.init();
            }

            if (window.MB.archiveToolbar) {
                window.MB.archiveToolbar.init();
            }

            if (window.MB.contactForm) {
                window.MB.contactForm.init();
            }

            // Bootstrap only auto-wires carousels on DOMContentLoaded, which a swap is
            // not — without this the arrows on a swapped-in carousel do nothing.
            if (window.MB.carousel) {
                window.MB.carousel.init();
            }
        }

        // Same guarantee as the inline script in <head>, for a page that never had one:
        // if reveal.js did not take ownership of the hidden state, show everything.
        window.setTimeout(() => {
            if (!root.hasAttribute('data-reveal-ready') && window.MB && window.MB.reveal) {
                window.MB.reveal.revealAll();
            }
        }, 2500);

        // A swap is not a document load, so gtag records nothing unless told. Fired here
        // and nowhere else: GA4's own history-based measurement must stay off, or every
        // navigation counts twice.
        if (typeof window.gtag === 'function' && settings.measurementId) {
            window.gtag('event', 'page_view', {
                send_to: settings.measurementId,
                page_location: window.location.href,
                page_title: document.title,
            });
        }
    });

    window.MB = window.MB || {};
    window.MB.swup = swup;
})();
